import { useState, useEffect } from "react";
import { Users, Mail, Database, BookOpen, Settings, Lock } from "lucide-react";
import { useOutletContext } from "react-router-dom";

import UserManagement from "../../components/Settings/UserManagement";
import SmtpConfiguration from "../../components/Settings/SMTPConfig";
import DatabaseManagementPage from "./database/DatabaseManagementPage";
import KnowledgeHub from "./knowledge/KnowledgeHub";
import AppSettingsTab from "./AppSettingsTab";
import RLSPolicyManagement from "./RLSPolicyManagement";
import { IngestionJobsProvider } from "./ingestion/IngestionJobsContext";

const tabs = [
  { key: "smtp",         label: "SMTP",         icon: Mail     },
  { key: "db",           label: "Database",     icon: Database },
  { key: "knowledge",    label: "Knowledge",    icon: BookOpen },
  { key: "app_settings", label: "App Settings", icon: Settings },
  { key: "users",        label: "Users",        icon: Users    },
  { key: "rls_policy",   label: "RLS Policy",   icon: Lock     },
];

// Maps ?tab= query-param values (from OnboardingWizard settingsPath)
// to the correct SettingsHub tab key + optional sub-tab string.
const TAB_PARAM_MAP = {
  "database":            { tab: "db",          subTab: null                   },
  "instructions":        { tab: "knowledge",   subTab: "instructions"         },
  "expressions":         { tab: "knowledge",   subTab: "expressions"          },
  "query-instructions":  { tab: "knowledge",   subTab: "query-instructions"   },
};

function getStateFromURL() {
  const params = new URLSearchParams(window.location.search);
  const tabParam = params.get("tab");
  if (!tabParam) return { tab: "users", subTab: null };
  if (TAB_PARAM_MAP[tabParam]) return TAB_PARAM_MAP[tabParam];
  // Direct key match fallback
  const exists = tabs.find((t) => t.key === tabParam);
  return { tab: exists ? tabParam : "users", subTab: null };
}

export default function SettingsHub() {
  const { refreshCurrentUser } = useOutletContext();
  const [activeTab, setActiveTab]     = useState(() => getStateFromURL().tab);
  const [activeSubTab, setActiveSubTab] = useState(() => getStateFromURL().subTab);

  // Sync when URL changes (browser back/forward, or after onNavigate pushState)
  useEffect(() => {
    function onPopState() {
      const { tab, subTab } = getStateFromURL();
      setActiveTab(tab);
      setActiveSubTab(subTab);
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const handleTabClick = (key) => {
    setActiveTab(key);
    setActiveSubTab(null);
  };

  const renderContent = () => {
    switch (activeTab) {
      case "smtp":
        return <SmtpConfiguration />;
      case "db":
        return <DatabaseManagementPage />;
      case "knowledge":
        // Pass initialSubTab so KnowledgeHub can open the right sub-tab
        return <KnowledgeHub initialSubTab={activeSubTab} />;
      case "app_settings":
        return <AppSettingsTab />;
      case "users":
        return <UserManagement />;
      case "rls_policy":
        return <RLSPolicyManagement />;
      default:
        return null;
    }
  };

  const isFullHeightTab = ["knowledge", "db"].includes(activeTab);

  return (
    <IngestionJobsProvider onCompleted={refreshCurrentUser}>
      <div className="h-screen flex flex-col" style={{ backgroundColor: "var(--background)" }}>

      {/* ================= COMPACT HEADER ================= */}
      <div
        className="flex-shrink-0 border-b shadow-sm"
        style={{ backgroundColor: "var(--background)", borderColor: "var(--border)" }}
      >
        <div className="px-3 sm:px-6 py-2 sm:py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 flex justify-start mt-1 sm:mt-2 overflow-hidden">
              <div
                className="inline-flex p-1 rounded-lg border overflow-x-auto no-scrollbar max-w-full"
                style={{ backgroundColor: "rgba(99, 102, 241, 0.06)", borderColor: "var(--border)" }}
              >
                {tabs.map(({ key, label, icon: Icon }) => {
                  const active = activeTab === key;
                  return (
                    <button
                      key={key}
                      onClick={() => handleTabClick(key)}
                      className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all shrink-0"
                      style={{
                        backgroundColor: active ? "var(--background)" : "transparent",
                        color: active ? "var(--foreground)" : "var(--muted-foreground)",
                        boxShadow: active ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                      }}
                    >
                      <Icon
                        className="w-4 h-4 shrink-0"
                        style={{ color: active ? "var(--foreground)" : "var(--muted-foreground)" }}
                      />
                      <span className="hidden sm:inline">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ================= CONTENT ================= */}
      <div
        className="flex-1 min-h-0 overflow-hidden"
        style={{ backgroundColor: "rgba(99, 102, 241, 0.04)" }}
      >
        {isFullHeightTab ? (
          <div className="h-full">{renderContent()}</div>
        ) : (
          <div className="no-scrollbar h-full overflow-y-auto">
            <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
              {renderContent()}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 footer-section">
        <div className="legal" style={{ color: "var(--muted-foreground)" }}>
          © 2026 ScatterPie Analytics Pvt. Ltd. •{" "}
          <a href="https://www.scatterpie.io/privacy-policy/" style={{ color: "var(--primary)" }}>
            Privacy Policy
          </a>
        </div>
      </div>

      </div>
    </IngestionJobsProvider>
  );
}
